import React from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { formatFullName } from '@/utils/common'

import { triggerToast } from '../../../components'
import { useDeleteUser } from '../hooks'
import { useUserManagementStore } from '../store'

export const DeleteUserDialog: React.FC = () => {
  const { t } = useTranslation()
  const { deletingUser, deleteDialogOpen, clearDeletingUser } =
    useUserManagementStore()
  const deleteUser = useDeleteUser()

  const handleClose = () => {
    clearDeletingUser()
  }

  const handleDelete = () => {
    if (!deletingUser) return

    deleteUser.mutate(deletingUser.id, {
      onSuccess: () => {
        triggerToast('success', t('userManagement.messages.deleteSuccess'))
        handleClose()
      },
      onError: () => {
        triggerToast('error', t('userManagement.messages.deleteError'))
      },
    })
  }

  const fullName = deletingUser
    ? formatFullName(deletingUser.first_name, deletingUser.last_name)
    : ''

  return (
    <Dialog open={deleteDialogOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[480px] bg-white shadow-xl rounded-2xl p-6">
        <DialogHeader className="space-y-0.5 pb-1">
          <DialogTitle className="m-0 font-semibold text-md text-primary-900">
            {t('userManagement.modal.deleteTitle')}
          </DialogTitle>
          <DialogDescription className="text-sm text-tertiary-600">
            {t('userManagement.modal.deleteDescription', { name: fullName })}
          </DialogDescription>
        </DialogHeader>

        <DialogFooter className="flex justify-stretch !space-x-0 gap-lg pt-3">
          <Button
            type="button"
            className="flex-1 rounded-md py-2.5 px-xl font-semibold text-secondary-700 hover:text-secondary-hover text-sm border-solid border-primary bg-white hover:bg-primary-hover"
            variant="default"
            onClick={handleClose}
            disabled={deleteUser.isPending}
          >
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleDelete}
            disabled={deleteUser.isPending}
            type="button"
            className="flex-1 rounded-md py-2.5 px-xl font-semibold text-sm border-solid border-transparent !text-white !bg-error-solid disabled:!bg-disabled disabled:!text-disabled"
            variant="default"
          >
            {t('common.delete')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
